import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Drawer from '../../ui/Drawer'
import EmptyState from '../../ui/EmptyState'
import Banner from '../../ui/Banner'
import Loader from '../../shared/Loader'
import DrawerInfoBanner from '../../shared/DrawerInfoBanner'
import CollectService from '../../../services/api/CollectService'
import DrawerToolbar from './DrawerToolbar'
import QuestionAccordion from './QuestionAccordion'

const EMPTY_MESSAGE = 'Les détails de cette section apparaîtront ici une fois les données disponibles.'
const NO_RESULTS_MESSAGE = 'Aucune question ne correspond à votre recherche.'
const ERROR_MESSAGE = 'Une erreur est survenue lors du chargement.'

const SectionDrawer = ({
  collectId, section, isOpen, onClose,
  gradeMax, mode, dateRange, granularity, resourcesHref,
  operatorId, resourceIds, resourceSearch, variableId
}) => {
  const [questions, setQuestions] = useState([])
  const [hasPhotoOrComment, setHasPhotoOrComment] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [openIds, setOpenIds] = useState(() => new Set())
  const [search, setSearch] = useState('')

  useEffect(() => {
    let cancelled = false
    if (isOpen && section) {
      setIsLoading(true)
      setHasError(false)
      setQuestions([])
      setHasPhotoOrComment(false)
      setOpenIds(new Set())
      setSearch('')
      CollectService.fetchSectionQuestions(collectId, section.id)
        .then(result => {
          if (cancelled)
            return
          const list = result?.questions || []
          setQuestions(list)
          setHasPhotoOrComment(Boolean(result?.has_photo_or_comment))
          setOpenIds(new Set(list.map(question => question.id)))
        })
        .catch(() => { if (!cancelled) {
          setQuestions([])
          setHasError(true)
        } })
        .finally(() => { if (!cancelled)
          setIsLoading(false) })
    }
    return () => { cancelled = true }
  }, [isOpen, collectId, section?.id])

  const filteredQuestions = useMemo(() => {
    if (!search.trim())
      return questions
    const needle = search.toLowerCase()
    return questions.filter(question => question.title.toLowerCase().includes(needle))
  }, [questions, search])

  const handleToggle = useCallback(questionId => {
    setOpenIds(previous => {
      const next = new Set(previous)
      if (next.has(questionId))
        next.delete(questionId)
      else
        next.add(questionId)
      return next
    })
  }, [])

  const allExpanded = filteredQuestions.length > 0
    && filteredQuestions.every(question => openIds.has(question.id))

  const handleToggleAll = useCallback(() => {
    if (allExpanded)
      setOpenIds(new Set())
    else
      setOpenIds(new Set(filteredQuestions.map(question => question.id)))
  }, [allExpanded, filteredQuestions])

  const handleSearchChange = useCallback(event => setSearch(event.target.value), [])

  const title = section ? `Détails de la section ${section.name}` : 'Détails de la section'
  const isDrawerOpen = isOpen && Boolean(section)
  const isReady = !isLoading && !hasError
  const showEmpty = isReady && questions.length === 0
  const showNoResults = isReady && questions.length > 0 && filteredQuestions.length === 0

  return (
    <Drawer
      ariaLabel={title}
      isOpen={isDrawerOpen}
      maxWidth={620}
      onClose={onClose}
    >
      <Drawer.Header onClose={onClose}>{title}</Drawer.Header>
      <Drawer.Body>
        {hasPhotoOrComment ? <DrawerInfoBanner resourcesHref={resourcesHref} /> : null}
        {questions.length > 0 ? (
          <DrawerToolbar
            allExpanded={allExpanded}
            search={search}
            onSearchChange={handleSearchChange}
            onToggleAll={handleToggleAll}
          />
        ) : null}
        {isLoading ? <Loader showText={false} size="small" /> : null}
        {!isLoading && hasError ? <Banner variant="danger">{ERROR_MESSAGE}</Banner> : null}
        {showEmpty ? <EmptyState message={EMPTY_MESSAGE} /> : null}
        {showNoResults ? <EmptyState message={NO_RESULTS_MESSAGE} /> : null}
        {isReady ? filteredQuestions.map(question => (
          <QuestionAccordion
            key={question.id}
            collectId={collectId}
            dateRange={dateRange}
            gradeMax={gradeMax}
            granularity={granularity}
            isOpen={openIds.has(question.id)}
            mode={mode}
            operatorId={operatorId}
            question={question}
            resourceIds={resourceIds}
            resourceSearch={resourceSearch}
            variableId={variableId}
            onToggle={handleToggle}
          />
        )) : null}
      </Drawer.Body>
    </Drawer>
  )
}

SectionDrawer.propTypes = {
  collectId: PropTypes.number.isRequired,
  dateRange: PropTypes.shape({
    endDate: PropTypes.string,
    startDate: PropTypes.string
  }).isRequired,
  gradeMax: PropTypes.number.isRequired,
  granularity: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['grade', 'satisfaction']).isRequired,
  onClose: PropTypes.func.isRequired,
  operatorId: PropTypes.string,
  resourceIds: PropTypes.arrayOf(PropTypes.number),
  resourceSearch: PropTypes.string,
  resourcesHref: PropTypes.string,
  section: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }),
  variableId: PropTypes.string
}

SectionDrawer.defaultProps = {
  operatorId: '',
  resourceIds: [],
  resourceSearch: '',
  resourcesHref: '#',
  section: null,
  variableId: ''
}

export default SectionDrawer
